import * as Sentry from '@sentry/react'
import { makeAutoObservable } from 'mobx'
import FingerPrint from '@fingerprintjs/fingerprintjs'
import { AuthService } from '~/shared/api'
import {
  AUTH_SESSION_CLEARED_EVENT,
  AUTH_SESSION_UPDATED_EVENT,
} from '~/shared/lib/constants/auth-events'
import {
  REFRESH_TOKEN_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from './config'
import { Handler, parseJSON } from '~/shared/lib'
import { ListUser, User } from '~/shared/api/services/users/types'
import { decodeJWT } from '~/shared/lib/helper-functions/decodeJWT'
import { removeAuthStorage } from '~/shared/lib/auth-session'

export class AuthModel {
  sessionToken = ''

  isLoading = false

  usersList: ListUser[] = []

  userById = null

  isListLoading = false

  isMoreLoading = false

  offset = 0

  limit = 20

  isEndUserList = false

  user: User | null = parseJSON(localStorage.getItem(USER_STORAGE_KEY)) || null

  tempHandlers: Handler[] = []

  legalEntity = ''

  role = []

  constructor() {
    makeAutoObservable(this)

    this.getSessionTokenFromStorage()
    document.addEventListener(
      AUTH_SESSION_UPDATED_EVENT,
      this.handleExternalSessionUpdate as EventListener,
    )
    document.addEventListener(
      AUTH_SESSION_CLEARED_EVENT,
      this.handleExternalSessionClear as EventListener,
    )
    window.addEventListener('storage', this.handleStorageChange)
  }

  setTempHandlers = (data: Handler[]) => {
    this.tempHandlers = data
  }

  get isAuth() {
    return !!this.sessionToken
  }

  private saveSessionTokenToStorage = (value: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, value)
    this.sessionToken = value
  }

  private saveUserToStorage = (value: User) => {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(value))
    this.user = value
  }

  private getSessionTokenFromStorage = async () => {
    const value = localStorage.getItem(TOKEN_STORAGE_KEY)

    if (value !== null) {
      this.sessionToken = value
      const decoded = decodeJWT(value)
      this.legalEntity = decoded?.lid || null
      this.role = decoded.rol || []
    }
  }

  private setSessionToken = (token: string) => {
    this.saveSessionTokenToStorage(token)
    this.sessionToken = token
    const decoded = decodeJWT(token)
    this.legalEntity = decoded?.lid || null
    this.role = decoded.rol || []
  }

  private clearSessionState = () => {
    this.sessionToken = ''
    this.user = null
    this.legalEntity = ''
    this.role = []
  }

  private clearAuthSession = () => {
    removeAuthStorage()
    this.clearSessionState()
    Sentry.setUser(null)
  }

  private handleExternalSessionUpdate = (
    event: CustomEvent<{ token?: string; refreshToken?: string }>,
  ) => {
    if (event.detail?.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, event.detail.refreshToken)
    }

    if (event.detail?.token) {
      this.setSessionToken(event.detail.token)
    }
  }

  private handleExternalSessionClear = () => {
    this.clearAuthSession()
  }

  private handleStorageChange = (event: StorageEvent) => {
    if (event.storageArea !== localStorage || event.key !== TOKEN_STORAGE_KEY) {
      return
    }

    if (event.newValue === null) {
      this.clearSessionState()
      Sentry.setUser(null)
      return
    }

    this.setSessionToken(event.newValue)
  }

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading
  }

  setProfile = (user: User) => {
    this.saveUserToStorage(user)
  }

  logout = async () => {
    try {
      const { status } = await AuthService.logout()

      if (status) {
        this.clearAuthSession()
      }
    } catch (error) {
      throw new Error('Unsuccessfully logout!')
    }
  }

  login = async (email: string, password: string) => {
    this.setIsLoading(true)

    try {
      const fpPromise = FingerPrint.load()
      const fp = await fpPromise
      const res = await fp.get()

      const payload = {
        email,
        password,
        platform: 'web',
        deviceId: res.visitorId,
      }
      const { data } = await AuthService.login(payload)

      this.setSessionToken(data.token)
      this.saveUserToStorage(data.user)
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refreshToken)
    } catch (error: unknown) {
      throw new Error('Invalid user data! Try again!')
    } finally {
      this.setIsLoading(false)
    }
  }
}

export default AuthModel
